import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import Layout from './Layout';
import CustomVideoPlayer from './CustomVideoPlayer';
import { Copy, Share2, Play, Film, Megaphone, Wifi, BarChart2, AlertTriangle, CalendarClock, Tv2, Wrench, MessageCircle, Send, Check, Zap, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';

// ── Analytics tracking helpers ─────────────────────────────────────────────
function detectDevice() {
  const ua = navigator.userAgent.toLowerCase();
  if (/smart-tv|smarttv|googletv|appletv|hbbtv|roku|firetv/.test(ua)) return 'smarttv';
  if (/ipad|tablet|(android(?!.*mobile))/.test(ua)) return 'tablet';
  if (/mobile|android|iphone|ipod|blackberry|windows phone/.test(ua)) return 'mobile';
  return 'desktop';
}

function trackSession(channelId, watchSeconds, watchPercent) {
  if (!channelId) return;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown';
  axios.post('/api/analytics/track', {
    channel_id: channelId,
    event_type: 'session_end',
    timezone,
    device_type: detectDevice(),
    watch_seconds: Math.round(watchSeconds),
    watch_percent: Math.round(watchPercent)
  }).catch(() => {});
}

// ── Chat Panel ─────────────────────────────────────────────────────────────
function ChatPanel({ channelId }) {
  const [messages, setMessages] = useState([]);
  const [name, setName] = useState(() => localStorage.getItem('chat_name') || '');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const messagesContainerRef = useRef(null);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`/api/chat/${channelId}`);
      setMessages(res.data.messages || []);
    } catch (_) {}
  };

  useEffect(() => {
    if (!channelId) return;
    fetchMessages();
    const interval = setInterval(fetchMessages, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId]);

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const nameColor = (n) => {
    const colors = ['#f87171','#fb923c','#fbbf24','#34d399','#38bdf8','#818cf8','#e879f9','#f472b6'];
    let h = 0;
    for (let i = 0; i < n.length; i++) h = (h * 31 + n.charCodeAt(i)) & 0xffff;
    return colors[h % colors.length];
  };

  const formatTime = (ts) => {
    const d = new Date(ts);
    return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!name.trim() || !text.trim() || sending) return;
    setSendError('');
    setSending(true);
    try {
      localStorage.setItem('chat_name', name.trim());
      await axios.post(`/api/chat/${channelId}`, { name: name.trim(), text: text.trim() });
      setText('');
      await fetchMessages();
    } catch (err) {
      setSendError(err.response?.data?.error || 'Error al enviar.');
      setTimeout(() => setSendError(''), 3000);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-full
                    bg-white dark:bg-gray-900
                    border border-gray-200 dark:border-gray-700
                    text-gray-900 dark:text-white
                    rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3
                      border-b border-gray-200 dark:border-gray-700">
        <MessageCircle size={16} className="text-primary flex-shrink-0" />
        <span className="text-sm font-semibold">Chat en vivo</span>
        <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">
          {messages.length} msgs
        </span>
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef}
           className="flex-1 overflow-y-auto p-3 space-y-2"
           style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(128,128,128,0.2) transparent' }}>
        {messages.length === 0 && (
          <div className="text-center text-xs mt-8 text-gray-400 dark:text-gray-500">
            Sin mensajes aún.<br />¡Sé el primero!
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className="text-sm leading-snug">
            <span className="text-[10px] mr-1.5 text-gray-400 dark:text-gray-600">{formatTime(msg.ts)}</span>
            <span className="font-semibold mr-1" style={{ color: nameColor(msg.name) }}>{msg.name}:</span>
            <span className="text-gray-700 dark:text-gray-300">{msg.text}</span>
          </div>
        ))}
      </div>

      {/* Input form */}
      <form onSubmit={handleSend}
            className="p-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
        <input
          type="text"
          placeholder="Tu nombre"
          maxLength={30}
          value={name}
          onChange={e => setName(e.target.value)}
          className="w-full px-3 py-1.5 rounded-lg text-xs
                     bg-gray-50 dark:bg-gray-800
                     border border-gray-300 dark:border-gray-700
                     text-gray-900 dark:text-white
                     placeholder-gray-400 dark:placeholder-gray-500
                     focus:outline-none focus:border-primary transition-colors"
        />
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Escribe un mensaje…"
            maxLength={300}
            value={text}
            onChange={e => setText(e.target.value)}
            className="flex-1 px-3 py-1.5 rounded-lg text-xs
                       bg-gray-50 dark:bg-gray-800
                       border border-gray-300 dark:border-gray-700
                       text-gray-900 dark:text-white
                       placeholder-gray-400 dark:placeholder-gray-500
                       focus:outline-none focus:border-primary transition-colors"
          />
          <button
            type="submit"
            disabled={!name.trim() || !text.trim() || sending}
            className="px-3 py-1.5 bg-primary hover:bg-primary-dark
                       disabled:opacity-40 disabled:cursor-not-allowed
                       text-white rounded-lg text-xs font-semibold
                       transition-all flex-shrink-0"
          >
            <Send size={13} />
          </button>
        </div>
        {sendError && (
          <p className="text-xs text-red-400">{sendError}</p>
        )}
      </form>
    </div>
  );
}

function EmbeddedPlayer({ user, onLogout }) {
  const { t } = useLanguage();
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const sessionStartRef = useRef(null);
  const trackedChannelRef = useRef(null);
  const [config, setConfig] = useState(null);
  const [channels, setChannels] = useState([]);
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const [playerConfig, setPlayerConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPublic, setIsPublic] = useState(false);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [autoplayEnabled, setAutoplayEnabled] = useState(false);
  const [mutedEnabled, setMutedEnabled] = useState(false);
  const [embedOptions, setEmbedOptions] = useState({
    width: 640,
    height: 360,
    autoplay: true,
    muted: false,
    chat: false
  });
  const [nowPlaying, setNowPlaying] = useState(null);
  const [nowPlayingLoading, setNowPlayingLoading] = useState(true);
  const [rtmpStats, setRtmpStats] = useState(null);
  const [unifiedUrlOnline, setUnifiedUrlOnline] = useState(null);
  const [playerBranding, setPlayerBranding] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [maintenanceMode, setMaintenanceMode] = useState(null); // null = loading, {enabled, message}
  const [chatEnabled, setChatEnabled] = useState(false);

  const fetchUserChannels = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/channels', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const userChannels = response.data.channels || [];
      setChannels(userChannels);

      // Si hay canales, seleccionar el primero automáticamente
      if (userChannels.length > 0) {
        setSelectedChannelId(userChannels[0].id);
        fetchConfig(userChannels[0].id);
      } else {
        // Si no hay canales, limpiar todo
        setConfig(null);
        setSelectedChannelId(null);
        // setError(null); // Removed error state
      }

      setLoading(false);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        window.dispatchEvent(new CustomEvent('auth:token-expired'));
        return;
      }
      console.error('Error fetching channels:', err);
      toast.error('No se pudieron cargar tus canales');
      setLoading(false);
    }
  };

  const fetchConfig = async (channelId, autoplay = false, muted = false) => {
    try {
      const token = localStorage.getItem('token');

      let configData;
      if (token) {
        // Admin: use authenticated channel endpoint
        const response = await axios.get(`/api/channels/${channelId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        configData = response.data.channel;
        // Attach stream URLs (unified HLS)
        if (configData?.stream_key) {
          configData.streamUrls = {
            hls: `${window.location.origin}/hls/${configData.stream_key}-master.m3u8`
          };
        }
      } else {
        // Public embed: use unauthenticated player config endpoint
        const response = await axios.get(`/api/player/config/${channelId}`);
        configData = response.data;
        // Fix protocol: replace server-generated http:// with current origin to avoid mixed-content blocks
        if (configData?.streamUrls?.hls) {
          configData.streamUrls.hls = configData.streamUrls.hls.replace(
            /^https?:\/\/[^/]+/,
            window.location.origin
          );
        }
      }

      if (!configData) {
        console.error('No channel data received');
        setLoading(false);
        return;
      }
      
      setPlayerConfig(configData);

      // Track session start
      if (configData?.id) {
        sessionStartRef.current = Date.now();
        trackedChannelRef.current = configData.id;
      }

      // Asegurar que el elemento video esté en el DOM antes de inicializar
      setTimeout(() => {
        if (configData) {
          initializePlayer(configData, autoplay, muted);
        }
      }, 500);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching channel config:', error);
      setLoading(false);
    }
  };

  const fetchNowPlaying = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get('/api/dashboard/now-playing', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNowPlaying(response.data);
    } catch (err) {
      // silent — don't show errors for this background poll
    } finally {
      setNowPlayingLoading(false);
    }
  };

  useEffect(() => {
    if (isPublic) return;
    fetchNowPlaying();
    const interval = setInterval(fetchNowPlaying, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPublic]);

  // Fetch player branding (watermark + ticker) – public endpoint
  useEffect(() => {
    axios.get('/api/settings/branding')
      .then(res => setPlayerBranding(res.data))
      .catch(() => setPlayerBranding(null));
  }, []);

  // Fetch maintenance mode – public endpoint
  useEffect(() => {
    axios.get('/api/settings/maintenance')
      .then(res => setMaintenanceMode({ enabled: Boolean(res.data.enabled), message: res.data.message || '' }))
      .catch(() => setMaintenanceMode({ enabled: false, message: '' }));
  }, []);

  const fetchRtmpStats = async () => {
    const streamKey = playerConfig?.stream_key;
    if (!streamKey || isPublic) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/rtmp/stream-stats/${streamKey}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setRtmpStats(response.data);
    } catch (_) {
      setRtmpStats(null);
    }
  };

  useEffect(() => {
    if (isPublic || !playerConfig?.stream_key) return;
    fetchRtmpStats();
    const interval = setInterval(fetchRtmpStats, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPublic, playerConfig?.stream_key]);

  // Poll unified URL to detect real online/offline status
  useEffect(() => {
    if (!playerConfig?.stream_key) return;
    const checkUnifiedUrl = async () => {
      try {
        const url = `/hls/${playerConfig.stream_key}-master.m3u8`;
        const res = await fetch(url, { cache: 'no-store' });
        if (!res.ok) { setUnifiedUrlOnline(false); return; }
        const text = await res.text();
        // Online if manifest has segments (#EXTINF = media playlist)
        // OR has variant streams (#EXT-X-STREAM-INF = master playlist with ABR)
        setUnifiedUrlOnline(text.includes('#EXTINF') || text.includes('#EXT-X-STREAM-INF'));
      } catch (_) {
        setUnifiedUrlOnline(false);
      }
    };
    checkUnifiedUrl();
    const interval = setInterval(checkUnifiedUrl, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerConfig?.stream_key]);

  const loadPlaylistVideo = (config, index, autoplay = false, muted = false) => {
    if (!config.playlistMode || !config.playlist?.items[index]) return;

    const item = config.playlist.items[index];
    const vod = item.vod;

    if (!vod || !vod.file_url) {
      // Skip to next video if current is unavailable
      handleNextVideo(config, index);
      return;
    }

    // Construct absolute URL
    let videoUrl = vod.file_url;
    if (!vod.file_url.startsWith('http')) {
      videoUrl = `${window.location.protocol}//${window.location.host}${vod.file_url}`;
    }

    // Determine MIME type
    const extension = vod.file_url.split('.').pop().toLowerCase();
    let mimeType = 'video/mp4';

    switch (extension) {
      case 'webm': mimeType = 'video/webm'; break;
      case 'mkv': mimeType = 'video/x-matroska'; break;
      case 'mov': mimeType = 'video/quicktime'; break;
      case 'avi': mimeType = 'video/x-msvideo'; break;
      case 'ogv': mimeType = 'video/ogg'; break;
      default: mimeType = 'video/mp4';
    }

    // If player exists, just update source and listeners (for playlist auto-advance)
    if (playerRef.current) {
      // Remove previous 'ended' listeners to avoid stacking
      playerRef.current.off('ended');
      playerRef.current.off('error');

      playerRef.current.src({ src: videoUrl, type: mimeType });
      playerRef.current.play().catch(e => console.error("Auto-play blocked:", e));

      // Re-attach listeners for playlist mode
      playerRef.current.on('ended', () => {
        handleNextVideo(config, index);
      });

      playerRef.current.on('error', () => {
        console.error('Video playback error, skipping to next');
        handleNextVideo(config, index);
      });
      return;
    }

    // Initialize new player if it doesn't exist
    setTimeout(() => {
      if (!videoRef.current) return;

      const player = videojs(videoRef.current, {
        controls: true,
        autoplay: autoplay,
        muted: muted,
        preload: 'auto',
        fluid: true,
        responsive: true,
        aspectRatio: '16:9',
        sources: [{
          src: videoUrl,
          type: mimeType
        }]
      });

      // Auto-advance to next video when current ends
      player.on('ended', () => {
        handleNextVideo(config, index);
      });

      // Error handling - skip to next video
      player.on('error', () => {
        console.error('Video playback error, skipping to next');
        handleNextVideo(config, index);
      });

      playerRef.current = player;
    }, 100);
  };

  const handleNextVideo = (config, currentIndex) => {
    if (!config.playlistMode || !config.playlist) return;

    const nextIndex = (currentIndex + 1) % config.playlist.items.length;
    setCurrentVideoIndex(nextIndex);
    loadPlaylistVideo(config, nextIndex, true, playerRef.current?.muted() || false);
  };

  const initializePlayer = (config, autoplay = false, muted = false) => {
    if (!videoRef.current || playerRef.current) return;

    // If playlist mode, load first video
    if (config.playlistMode && config.playlist?.items.length > 0) {
      loadPlaylistVideo(config, 0, autoplay, muted);
      return;
    }

    // Single video or HLS mode
    const playerOptions = {
      controls: true,
      autoplay: autoplay,
      muted: muted,
      preload: 'auto',
      fluid: true,
      responsive: true,
      aspectRatio: '16:9'
    };

    // Configurar fuentes según el tipo de canal
    let source = {};
    if (config.type === 'vod' && config.streamUrls?.mp4) {
      source = { src: config.streamUrls.mp4, type: 'video/mp4' };
    } else if (config.streamUrls?.hls) {
      source = { src: config.streamUrls.hls, type: 'application/x-mpegURL' };
    }

    // Hard Reset Strategy: Always create new player here
    if (playerRef.current) {
      playerRef.current.dispose();
      playerRef.current = null;
    }

    playerOptions.sources = [source];

    const player = videojs(videoRef.current, playerOptions);
    playerRef.current = player;
  };

  // Track session end on unmount
  useEffect(() => {
    return () => {
      const channelId = trackedChannelRef.current;
      const startTime = sessionStartRef.current;
      if (channelId && startTime) {
        const watchSeconds = (Date.now() - startTime) / 1000;
        trackSession(channelId, watchSeconds, Math.min(100, (watchSeconds / 3600) * 100));
      }
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const channelIdFromUrl = params.get('channel');
    const hlsUrlFromUrl = params.get('hls');
    const autoplay = params.get('autoplay') === 'true';
    const muted = params.get('muted') === 'true';
    const chat = params.get('chat') === '1';
    const theme = params.get('theme'); // 'light' or 'dark' (default)

    // Apply theme override from URL param
    if (theme === 'light') {
      document.documentElement.classList.remove('dark');
    } else if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    }

    const isPublicView = !!channelIdFromUrl || !!hlsUrlFromUrl;
    setIsPublic(isPublicView);
    setAutoplayEnabled(autoplay);
    setMutedEnabled(muted);
    setChatEnabled(chat);

    if (hlsUrlFromUrl) {
      // Modo HLS directo - construir URL completa del master.m3u8
      setSelectedChannelId('hls-direct');
      const hlsMasterUrl = `${window.location.origin}/api/hls/playlist/${hlsUrlFromUrl}/master.m3u8`;
      const hlsConfig = {
        type: 'vod',
        streamUrls: {
          hls: hlsMasterUrl
        },
        status: 'online'
      };
      setPlayerConfig(hlsConfig);
      setCurrentVideoIndex(0);

      setTimeout(() => {
        if (videoRef.current && !playerRef.current) {
          initializePlayer(hlsConfig, autoplay, muted);
        }
      }, 300);
      setLoading(false);
    } else if (isPublicView) {
      setSelectedChannelId(channelIdFromUrl); // Establecer el canal seleccionado
      fetchConfig(channelIdFromUrl, autoplay, muted);
    } else {
      fetchUserChannels();
    }

    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChannelChange = (e) => {
    const channelId = e.target.value;
    setSelectedChannelId(channelId);

    // Si no hay canal seleccionado, volver al estado inicial
    if (!channelId) {
      setPlayerConfig(null);
      setLoading(false);
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
      return;
    }

    setLoading(true);

    // Hard Reset Strategy:
    // 1. Dispose player immediately
    if (playerRef.current) {
      playerRef.current.dispose();
      playerRef.current = null;
    }
    // 2. Clear config to remove <video> from DOM
    setPlayerConfig(null);

    // 3. Fetch new config (which will trigger re-render and re-init)
    fetchConfig(channelId);
  };

  const initPlayer = (source, autoplay, muted) => {
    if (!videoRef.current) return;

    if (playerRef.current) {
      playerRef.current.src(source);
      return;
    }

    const player = videojs(videoRef.current, {
      autoplay: autoplay,
      muted: muted,
      controls: true,
      responsive: true,
      fluid: true,
      preload: 'auto',
      liveui: source.type === 'application/x-mpegURL',
      sources: [source]
    });

    playerRef.current = player;
  };

  const getEmbedUrl = () => {
    if (!selectedChannelId) return '';
    const host = window.location.origin;
    
    if (selectedChannelId === 'hls-direct') {
      // Si ya es una URL de playlist ID, pasar solo el ID
      return `${host}/player?hls=${playerConfig?.playlist_id || ''}&autoplay=${embedOptions.autoplay}&muted=${embedOptions.muted}`;
    }
    
    return `${host}/player?channel=${selectedChannelId}&autoplay=${embedOptions.autoplay}&muted=${embedOptions.muted}${embedOptions.chat ? '&chat=1' : ''}`;
  };

  const getIframeCode = () => {
    if (!selectedChannelId) return '';
    const w = embedOptions.chat ? Math.max(embedOptions.width, 960) : embedOptions.width;
    return `<iframe src="${getEmbedUrl()}" width="${w}" height="${embedOptions.height}" frameborder="0" allowfullscreen allow="autoplay; encrypted-media"></iframe>`;
  };

  const copyToClipboard = (text, label, fieldKey) => {
    const onSuccess = () => {
      toast.success(`${label} copiado`);
      if (fieldKey) {
        setCopiedField(fieldKey);
        setTimeout(() => setCopiedField(null), 2000);
      }
    };
    // Fallback para HTTP que no soporta navigator.clipboard
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(onSuccess).catch(() => {
        fallbackCopy(text, label, fieldKey);
      });
    } else {
      fallbackCopy(text, label, fieldKey);
    }
  };

  const fallbackCopy = (text, label, fieldKey) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      toast.success(`${label} copiado`);
      if (fieldKey) {
        setCopiedField(fieldKey);
        setTimeout(() => setCopiedField(null), 2000);
      }
    } catch (err) {
      toast.error('Error al copiar');
    }

    document.body.removeChild(textArea);
  };

  if (loading && isPublic) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-black text-white">
        <div className="loading"></div>
      </div>
    );
  }

  const playerContent = (
    <div className={`w-full bg-black relative overflow-hidden
                     ${isPublic && !chatEnabled ? 'h-screen rounded-none border-none' :
                       isPublic && chatEnabled  ? 'h-full rounded-none border-none' :
                       'aspect-video rounded-xl border border-gray-700'}`}>
      {playerConfig && selectedChannelId ? (
        <CustomVideoPlayer
          key={selectedChannelId}
          src={playerConfig.streamUrls?.hls || ''}
          type="application/x-mpegURL"
          autoplay={isPublic ? autoplayEnabled : false}
          muted={isPublic ? mutedEnabled : false}
          isLive={playerConfig.type === 'live' || playerConfig.type === 'relay' || playerConfig.status === 'online'}
          className="w-full h-full"
          watermark={playerBranding?.watermark || null}
          ticker={playerBranding?.ticker || null}
          transitions={playerBranding?.transitions || null}
          sloopVideo={playerBranding?.sloop || null}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center p-8">
          {channels.length === 0 && !isPublic ? (
            /* No channels created yet */
            <div className="flex flex-col items-center text-center max-w-sm">
              {/* Icon with glow */}
              <div className="relative mb-6">
                <div className="absolute inset-0 rounded-full bg-accent-purple/20 blur-2xl scale-150" />
                <div className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-accent-purple/30 to-accent-purple/10 border border-accent-purple/30 flex items-center justify-center backdrop-blur-sm">
                  <Tv2 size={36} className="text-accent-purple" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Sin canales configurados</h3>
              <p className="text-sm text-gray-400 leading-relaxed mb-6">
                Crea tu primer canal para comenzar a transmitir y previsualizar tu señal en vivo.
              </p>
              <a href="/channels"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-accent-purple hover:bg-accent-purple/90 text-white text-sm font-semibold transition-all duration-200 shadow-lg shadow-accent-purple/25">
                <Play size={15} />
                Crear canal
              </a>
            </div>
          ) : (
            /* Channel exists but no signal */
            <div className="flex flex-col items-center text-center max-w-sm">
              <div className="relative mb-6">
                <div className="absolute inset-0 rounded-full bg-white/5 blur-2xl scale-150" />
                <div className="relative w-20 h-20 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center backdrop-blur-sm">
                  <Wifi size={36} className="text-gray-500" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                {isPublic ? 'Conectando...' : 'Sin señal de video'}
              </h3>
              <p className="text-sm text-gray-400 leading-relaxed">
                {isPublic ? 'Por favor espera un momento mientras cargamos la señal.' : 'Selecciona un canal o activa una playlist para comenzar.'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (isPublic) {
    // Show maintenance screen for public viewers when enabled
    if (maintenanceMode?.enabled) {
      return (
        <div className="w-screen h-screen flex items-center justify-center bg-black text-white">
          <div className="flex flex-col items-center gap-6 text-center px-8 max-w-sm">
            <div className="w-20 h-20 rounded-2xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
              <Wrench size={38} className="text-amber-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white mb-3">En Mantenimiento</h1>
              <p className="text-gray-400 text-base leading-relaxed">
                {maintenanceMode.message || 'Estamos realizando tareas de mantenimiento. Volvemos pronto.'}
              </p>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" />
              Volveremos pronto
            </div>
          </div>
        </div>
      );
    }
    if (chatEnabled && selectedChannelId) {
      return (
        <div className="dark w-screen h-screen flex flex-col sm:flex-row bg-black overflow-hidden">
          <div className="flex-1 min-h-0">{playerContent}</div>
          <div className="w-full sm:w-80 flex-shrink-0 h-48 sm:h-full">
            <ChatPanel channelId={selectedChannelId} />
          </div>
        </div>
      );
    }
    return playerContent;
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      {/* Maintenance Mode banner (admin view) */}
      {maintenanceMode?.enabled && (
        <div className="mx-2.5 mt-2.5 flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-500/30">
          <Wrench size={16} className="text-amber-400 flex-shrink-0" />
          <p className="text-sm text-amber-300 font-medium">
            <span className="font-bold">Modo mantenimiento activo</span> — los espectadores públicos ven la pantalla de mantenimiento.
          </p>
        </div>
      )}

      <div className="p-2.5">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-start gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-2 text-gray-900 dark:text-white">
              Reproductor
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Previsualiza tus canales y obtén códigos de inserción
            </p>
          </div>

          {channels.length > 0 && (
            <div className="w-full sm:w-72">
              <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
                Seleccionar Canal
              </label>
              <select className="w-full px-4 py-3 bg-gray-50 dark:bg-dark-lighter
                               border border-gray-300 dark:border-gray-700
                               rounded-lg text-gray-900 dark:text-white text-base
                               transition-all duration-200 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                      value={selectedChannelId}
                      onChange={handleChannelChange}>
                {channels.map(ch => (
                  <option key={ch.id} value={ch.id}>{ch.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 flex flex-col gap-6">
            {playerContent}

            {/* RTMP Stats Bar — solo visible cuando un dispositivo externo (OBS) está enviando señal */}
            {!isPublic && rtmpStats?.active && (
              <div className="glass-card flex flex-wrap items-center gap-x-5 gap-y-2 px-4 py-3">
                {/* Tipo + Health */}
                <div className="flex items-center gap-2 shrink-0">
                  <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-blue-500/10 border border-blue-500/20">
                    <Wifi size={12} className="text-blue-400" />
                    <span className="text-xs font-bold text-blue-400 uppercase tracking-wider">RTMP</span>
                  </div>
                  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider border
                    ${rtmpStats.health === 'EXCELENTE' ? 'bg-green-500/15 text-green-400 border-green-500/30' :
                      rtmpStats.health === 'BUENA'      ? 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' :
                                                         'bg-red-500/15 text-red-400 border-red-500/30'}`}>
                    <div className={`w-1.5 h-1.5 rounded-full animate-pulse
                      ${rtmpStats.health === 'EXCELENTE' ? 'bg-green-400' :
                        rtmpStats.health === 'BUENA'      ? 'bg-yellow-400' : 'bg-red-400'}`} />
                    {rtmpStats.health}
                  </div>
                </div>

                <div className="hidden sm:block w-px h-5 bg-white/10" />

                {/* Resolución */}
                <div className="flex flex-col">
                  <span className="text-[10px] text-gray-500 uppercase tracking-wider leading-none">Resolución</span>
                  <span className="text-sm font-bold text-white leading-tight">{rtmpStats.resolution}</span>
                </div>

                {/* Bitrate + alerta si excede el límite */}
                <div className="flex flex-col">
                  <span className="text-[10px] text-gray-500 uppercase tracking-wider leading-none">Bitrate</span>
                  <span className={`text-sm font-bold leading-tight ${
                    rtmpStats.bitrateAlert?.exceeds ? 'text-orange-400' : 'text-white'
                  }`}>
                    {rtmpStats.bitrate.toLocaleString()} Kbps
                  </span>
                </div>

                {/* Alerta de bitrate excedido */}
                {rtmpStats.bitrateAlert?.exceeds && (
                  <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg
                                  bg-orange-500/15 border border-orange-500/30 text-orange-400">
                    <AlertTriangle size={13} className="shrink-0" />
                    <span className="text-[11px] font-semibold leading-tight">
                      {rtmpStats.bitrate.toLocaleString()} &gt; {rtmpStats.bitrateAlert.limit.toLocaleString()} kbps
                    </span>
                  </div>
                )}

                {/* FPS */}
                <div className="flex flex-col">
                  <span className="text-[10px] text-gray-500 uppercase tracking-wider leading-none">Frame rate</span>
                  <span className="text-sm font-bold text-white leading-tight">{rtmpStats.fps} fps</span>
                </div>

                {/* Keyframe interval */}
                <div className="flex flex-col">
                  <span className="text-[10px] text-gray-500 uppercase tracking-wider leading-none">Keyframe</span>
                  <span className="text-sm font-bold text-white leading-tight">{rtmpStats.keyframeInterval} seg</span>
                </div>

                {/* Analytics link */}
                <div className="ml-auto">
                  <a href="/analytics"
                     className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg
                                bg-primary/10 hover:bg-primary/20 border border-primary/30
                                text-primary text-xs font-semibold transition-all duration-200">
                    <BarChart2 size={14} />
                    <span className="hidden sm:inline">Analítica</span>
                  </a>
                </div>
              </div>
            )}

            {/* Reproduciendo Ahora */}
            {!nowPlayingLoading && nowPlaying && nowPlaying.isPlaylist && nowPlaying.nowPlaying && nowPlaying.nowPlaying.current && (
              <div className="glass-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-accent-purple/20 to-accent-indigo/20 backdrop-blur-sm flex items-center justify-center border border-accent-purple/30">
                    <Play className="text-accent-purple" size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Reproduciendo Ahora</h3>
                    <p className="text-xs text-gray-400">
                      {nowPlaying.channel.name} • Loop #{nowPlaying.nowPlaying.loopCount + 1}
                    </p>
                  </div>
                </div>

                {/* Video actual */}
                <div className="glass-card mb-3 border border-accent-purple/20">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-accent-purple/30 to-accent-indigo/30 backdrop-blur-sm flex items-center justify-center flex-shrink-0 border border-accent-purple/40">
                      {nowPlaying.nowPlaying.current.is_spot
                        ? <Megaphone className="text-accent-purple" size={20} />
                        : <Film className="text-accent-purple" size={20} />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-white truncate">
                        {nowPlaying.nowPlaying.current.title}
                      </div>
                      <div className="text-xs text-gray-400">
                        {Math.floor(nowPlaying.nowPlaying.current.progress / 60)}:{String(Math.floor(nowPlaying.nowPlaying.current.progress % 60)).padStart(2, '0')} / {Math.floor(nowPlaying.nowPlaying.current.duration / 60)}:{String(Math.floor(nowPlaying.nowPlaying.current.duration % 60)).padStart(2, '0')}
                      </div>
                    </div>
                    <div className="text-lg font-bold bg-gradient-to-r from-accent-purple to-accent-indigo bg-clip-text text-transparent">
                      {nowPlaying.nowPlaying.current.progressPercent}%
                    </div>
                  </div>
                  <div className="relative w-full h-1.5 bg-white/5 rounded-full overflow-hidden mt-2">
                    <div
                      className="h-1.5 bg-gradient-to-r from-accent-purple via-accent-indigo to-accent-purple rounded-full transition-all duration-1000"
                      style={{ width: `${nowPlaying.nowPlaying.current.progressPercent}%` }}
                    ></div>
                  </div>
                </div>

                {/* Próximos en cola */}
                <div>
                  <h4 className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider">
                    Próximos en cola
                  </h4>
                  <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.1) transparent' }}>
                    {(() => {
                      const scheduledPlaylists = nowPlaying.nowPlaying.scheduledPlaylists || [];
                      const upcoming = nowPlaying.nowPlaying.upcoming;
                      const current = nowPlaying.nowPlaying.current;
                      const remainingCurrent = (current.duration || 0) - (current.progress || 0);
                      let nextSched = null;
                      if (scheduledPlaylists.length > 0) {
                        const now = new Date();
                        const dayNames = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
                        const curMins = now.getHours() * 60 + now.getMinutes();
                        let minSecs = Infinity;
                        for (const pl of scheduledPlaylists) {
                          if (!pl.days || !pl.start_time) continue;
                          const [sh, sm] = pl.start_time.split(':').map(Number);
                          const startMins = sh * 60 + sm;
                          for (let d = 0; d < 7; d++) {
                            const day = dayNames[(now.getDay() + d) % 7];
                            if (!pl.days.includes(day)) continue;
                            const minsUntil = d === 0 ? startMins - curMins : d * 1440 - curMins + startMins;
                            if (minsUntil <= 0) continue;
                            const secsUntil = minsUntil * 60;
                            if (secsUntil < minSecs) {
                              minSecs = secsUntil;
                              const secsAfterCurrent = secsUntil - remainingCurrent;
                              let pos = secsAfterCurrent <= 0 ? 0 : -1;
                              if (pos < 0) {
                                let acc = 0;
                                for (let i = 0; i < upcoming.length; i++) {
                                  acc += upcoming[i].duration || 0;
                                  if (acc >= secsAfterCurrent) { pos = i + 1; break; }
                                }
                              }
                              if (pos >= 0) {
                                const h = Math.floor(secsUntil / 3600);
                                const m = Math.floor((secsUntil % 3600) / 60);
                                nextSched = { playlist_name: pl.name, seconds_until: secsUntil, transition_at_position: pos, timeLabel: h > 0 ? `en ${h}h ${m}m` : `en ${m}m` };
                              }
                            }
                            break;
                          }
                        }
                      }
                      const items = [];
                      upcoming.forEach((item, index) => {
                        if (nextSched && index === nextSched.transition_at_position) {
                          items.push(
                            <div key="sched-divider" className="flex items-center gap-2 py-1">
                              <div className="h-px flex-1 bg-amber-500/40"></div>
                              <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30">
                                <CalendarClock size={10} className="text-amber-400 flex-shrink-0" />
                                <span className="text-xs text-amber-400 font-semibold whitespace-nowrap">
                                  {nextSched.playlist_name} · {nextSched.timeLabel}
                                </span>
                              </div>
                              <div className="h-px flex-1 bg-amber-500/40"></div>
                            </div>
                          );
                        }
                        items.push(
                          <div
                            key={index}
                            className={`flex items-center gap-2 p-2 rounded-lg hover:bg-white/10 transition-all ${
                              item.is_avance
                                ? 'bg-sky-500/10 border border-sky-500/20'
                                : item.is_spot
                                  ? 'bg-accent-purple/10 border border-accent-purple/20'
                                  : 'bg-white/5'
                            }`}
                          >
                            <div className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                              item.is_avance ? 'bg-sky-500/40 text-sky-400' : item.is_spot ? 'bg-accent-purple/40 text-accent-purple' : 'bg-white/10 text-gray-400'
                            }`}>
                              {index + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className={`text-xs font-medium truncate ${
                                item.is_avance ? 'text-sky-400' : item.is_spot ? 'text-accent-purple' : 'text-white'
                              }`}>
                                {item.is_avance && <Tv2 size={11} className="mr-1 flex-shrink-0 inline" />}
                                {item.is_spot && !item.is_avance && <span className="mr-1">★</span>}
                                {item.title}
                              </div>
                            </div>
                            <div className="text-xs text-gray-400 flex-shrink-0">
                              {Math.floor(item.duration / 60)}:{String(item.duration % 60).padStart(2, '0')}
                            </div>
                          </div>
                        );
                      });
                      return items;
                    })()}
                  </div>
                </div>
              </div>
            )}
          </div>

          {channels.length > 0 && (
            <div className="flex flex-col gap-6">
              {selectedChannelId && (
                <div className="h-96">
                  <ChatPanel channelId={selectedChannelId} />
                </div>
              )}

              <div className="glass-card p-6">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-accent-purple to-accent-indigo flex items-center justify-center shadow-lg shadow-accent-purple/30 flex-shrink-0">
                    <Share2 size={16} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-white">Compartir Canal</h3>
                    <p className="text-xs text-gray-400">Embed, links y opciones del player</p>
                  </div>
                </div>

                {/* Toggle options */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {/* Autoplay */}
                  <button
                    onClick={() => setEmbedOptions({ ...embedOptions, autoplay: !embedOptions.autoplay })}
                    className={`flex flex-col items-center gap-2 p-3 rounded-xl border transition-all duration-200 cursor-pointer ${embedOptions.autoplay ? 'bg-accent-purple/15 border-accent-purple/40 text-accent-purple' : 'bg-white/4 border-white/8 text-gray-400 hover:bg-white/8'}`}
                  >
                    <Play size={16} className={embedOptions.autoplay ? 'text-accent-purple' : 'text-gray-500'} />
                    <span className="text-xs font-medium">Autoplay</span>
                    <div className={`w-8 h-4 rounded-full transition-all duration-200 relative ${embedOptions.autoplay ? 'bg-accent-purple' : 'bg-white/15'}`}>
                      <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all duration-200 ${embedOptions.autoplay ? 'right-0.5' : 'left-0.5'}`} />
                    </div>
                  </button>
                  {/* Silenciar */}
                  <button
                    onClick={() => setEmbedOptions({ ...embedOptions, muted: !embedOptions.muted })}
                    className={`flex flex-col items-center gap-2 p-3 rounded-xl border transition-all duration-200 cursor-pointer ${embedOptions.muted ? 'bg-amber-500/15 border-amber-500/40 text-amber-400' : 'bg-white/4 border-white/8 text-gray-400 hover:bg-white/8'}`}
                  >
                    {embedOptions.muted ? <VolumeX size={16} className="text-amber-400" /> : <Volume2 size={16} className="text-gray-500" />}
                    <span className="text-xs font-medium">Silenciar</span>
                    <div className={`w-8 h-4 rounded-full transition-all duration-200 relative ${embedOptions.muted ? 'bg-amber-500' : 'bg-white/15'}`}>
                      <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all duration-200 ${embedOptions.muted ? 'right-0.5' : 'left-0.5'}`} />
                    </div>
                  </button>
                  {/* Incluir chat */}
                  <button
                    onClick={() => setEmbedOptions({ ...embedOptions, chat: !embedOptions.chat })}
                    className={`flex flex-col items-center gap-2 p-3 rounded-xl border transition-all duration-200 cursor-pointer ${embedOptions.chat ? 'bg-cyan-500/15 border-cyan-500/40 text-cyan-400' : 'bg-white/4 border-white/8 text-gray-400 hover:bg-white/8'}`}
                  >
                    <MessageCircle size={16} className={embedOptions.chat ? 'text-cyan-400' : 'text-gray-500'} />
                    <span className="text-xs font-medium">Chat</span>
                    <div className={`w-8 h-4 rounded-full transition-all duration-200 relative ${embedOptions.chat ? 'bg-cyan-500' : 'bg-white/15'}`}>
                      <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all duration-200 ${embedOptions.chat ? 'right-0.5' : 'left-0.5'}`} />
                    </div>
                  </button>
                </div>

                {/* URL del Player */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">URL del Player</span>
                  </div>
                  <div className="flex gap-2">
                    <input
                      className="flex-1 px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-gray-200 text-xs font-mono focus:outline-none focus:border-accent-purple/50 focus:bg-white/8 transition-all"
                      readOnly
                      value={getEmbedUrl()} />
                    <button
                      className={`px-3 rounded-xl border transition-all duration-200 flex items-center justify-center flex-shrink-0 ${copiedField === 'player-url' ? 'bg-green-500/20 border-green-500/40 text-green-400' : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10 hover:text-white'}`}
                      onClick={() => copyToClipboard(getEmbedUrl(), 'URL', 'player-url')}>
                      {copiedField === 'player-url' ? <Check size={16} /> : <Copy size={16} />}
                    </button>
                  </div>
                </div>

                {/* Código iFrame */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Código iFrame</span>
                  </div>
                  <textarea
                    className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-gray-300 text-xs font-mono focus:outline-none focus:border-accent-purple/50 focus:bg-white/8 transition-all resize-none mb-2"
                    readOnly
                    rows="3"
                    value={getIframeCode()} />
                  <button
                    className={`w-full px-4 py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-200 ${copiedField === 'iframe' ? 'bg-green-500/20 border border-green-500/40 text-green-400' : 'btn-primary-gradient'}`}
                    onClick={() => copyToClipboard(getIframeCode(), 'Código iFrame', 'iframe')}>
                    {copiedField === 'iframe' ? <Check size={16} /> : <Copy size={16} />}
                    {copiedField === 'iframe' ? '¡Copiado!' : 'Copiar iFrame'}
                  </button>
                </div>

                {/* URL HLS Unificada */}
                {playerConfig?.stream_key && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">URL HLS Unificada</span>
                      <div className="flex items-center gap-1 px-2 py-0.5 bg-accent-purple/15 border border-accent-purple/25 rounded-full">
                        <Zap size={10} className="text-accent-purple" />
                        <span className="text-[10px] font-semibold text-accent-purple">HLS</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <input
                        className="flex-1 px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-gray-200 text-xs font-mono focus:outline-none focus:border-accent-purple/50 focus:bg-white/8 transition-all"
                        readOnly
                        value={`${window.location.origin}/hls/${playerConfig.stream_key}-master.m3u8`} />
                      <button
                        className={`px-3 rounded-xl border transition-all duration-200 flex items-center justify-center flex-shrink-0 ${copiedField === 'hls-url' ? 'bg-green-500/20 border-green-500/40 text-green-400' : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10 hover:text-white'}`}
                        onClick={() => copyToClipboard(`${window.location.origin}/hls/${playerConfig.stream_key}-master.m3u8`, 'URL HLS', 'hls-url')}>
                        {copiedField === 'hls-url' ? <Check size={16} /> : <Copy size={16} />}
                      </button>
                    </div>
                  </div>
                )}
              </div>

            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default EmbeddedPlayer;
